import Vue from 'vue'
import Vuex from 'vuex'
import { TOKEN_KEY, USER_INFO_KEY } from '@/constants'
import { getContestDetail, getCommonContestDetail } from '@/api/admin'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    token: localStorage.getItem(TOKEN_KEY) || '',
    userInfo: (() => {
      const storedUserInfo = localStorage.getItem(USER_INFO_KEY);
      // 检查是否为 null、undefined 或 "undefined" 字符串
      if (!storedUserInfo || storedUserInfo === 'undefined' || storedUserInfo === 'null') {
        return null;
      }
      try {
        return JSON.parse(storedUserInfo);
      } catch (e) {
        console.error('Failed to parse userInfo from localStorage:', e);
        return null;
      }
    })(),
    // 活动配置（从后端获取）
    contestConfig: null
  },
  
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
      localStorage.setItem(TOKEN_KEY, token)
    },
    
    SET_USER_INFO(state, userInfo) {
      state.userInfo = userInfo
      localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo))
    },

    UPDATE_TEACHER_AUTH_STATUS(state, status) {
      if (state.userInfo) {
        state.userInfo.teacherAuth = status
        localStorage.setItem(USER_INFO_KEY, JSON.stringify(state.userInfo))
      }
      // 同时更新本地存储中的用户信息
      const homelabUserInfo = localStorage.getItem('homelab_user_info')
      if (homelabUserInfo) {
        try {
          const userInfo = JSON.parse(homelabUserInfo)
          userInfo.teacherAuth = status
          localStorage.setItem('homelab_user_info', JSON.stringify(userInfo))
        } catch (e) {
          console.error('Failed to update teacher auth status in homelab_user_info:', e)
        }
      }
    },

    UPDATE_SCHOOL_ADMIN_AUTH_STATUS(state, status) {
      if (state.userInfo) {
        state.userInfo.schoolAdminAuth = status
        localStorage.setItem(USER_INFO_KEY, JSON.stringify(state.userInfo))
      }
      // 同时更新本地存储中的用户信息
      const homelabUserInfo = localStorage.getItem('homelab_user_info')
      if (homelabUserInfo) {
        try {
          const userInfo = JSON.parse(homelabUserInfo)
          userInfo.schoolAdminAuth = status
          localStorage.setItem('homelab_user_info', JSON.stringify(userInfo))
        } catch (e) {
          console.error('Failed to update school admin auth status in homelab_user_info:', e)
        }
      }
    },

    UPDATE_AUTH_STATUS(state, status) {
      if (state.userInfo) {
        state.userInfo.authStatus = status
        localStorage.setItem(USER_INFO_KEY, JSON.stringify(state.userInfo))
      }
      // 同时更新本地存储中的用户信息
      const homelabUserInfo = localStorage.getItem('homelab_user_info')
      if (homelabUserInfo) {
        try {
          const userInfo = JSON.parse(homelabUserInfo)
          userInfo.authStatus = status
          localStorage.setItem('homelab_user_info', JSON.stringify(userInfo))
        } catch (e) {
          console.error('Failed to update auth status in homelab_user_info:', e)
        }
      }
    },

    UPDATE_REAL_NAME(state, realName) {
      if (state.userInfo) {
        state.userInfo.realName = realName
        localStorage.setItem(USER_INFO_KEY, JSON.stringify(state.userInfo))
        localStorage.setItem('realName', realName)
      }
      // 同时更新本地存储中的用户信息
      const homelabUserInfo = localStorage.getItem('homelab_user_info')
      if (homelabUserInfo) {
        try {
          const userInfo = JSON.parse(homelabUserInfo)
          userInfo.realName = realName
          localStorage.setItem('homelab_user_info', JSON.stringify(userInfo))
        } catch (e) {
          console.error('Failed to update real name in homelab_user_info:', e)
        }
      }
    },
    
    CLEAR_AUTH(state) {
      state.token = ''
      state.userInfo = null
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_INFO_KEY)
      // 清除额外存储的信息
      localStorage.removeItem('LOGIN_NAME')
      localStorage.removeItem('ROLE_CODE')
      localStorage.removeItem('realName')
      localStorage.removeItem('homelab_token')
      localStorage.removeItem('homelab_user_info')
      localStorage.removeItem('USER_ID')


    },

    SET_CONTEST_CONFIG(state, config) {
      state.contestConfig = config
      // 可选：持久化到 localStorage
      localStorage.setItem('contest_config', JSON.stringify(config))
    }
  },
  
  actions: {
    // 登录
    login({ commit }, userInfo) {
      return new Promise((resolve, reject) => {
        // 仅设置token和userInfo到state中
        commit('SET_TOKEN', userInfo.token)
        commit('SET_USER_INFO', userInfo.userInfo)
        
        // 仅当homelab_user_info不存在时才保存到localStorage
        if (userInfo.userInfo) {
          const existingUserInfo = localStorage.getItem('homelab_user_info');
          if (!existingUserInfo) {
            localStorage.setItem('homelab_user_info', JSON.stringify(userInfo.userInfo));
          }
        }
        
        resolve()
      })
    },

    // 更新老师认证状态
    updateTeacherAuthStatus({ commit }, status) {
      commit('UPDATE_TEACHER_AUTH_STATUS', status)
    },

    // 更新学校管理员认证状态
    updateSchoolAdminAuthStatus({ commit }, status) {
      commit('UPDATE_SCHOOL_ADMIN_AUTH_STATUS', status)
    },

    // 更新认证状态
    updateAuthStatus({ commit }, status) {
      commit('UPDATE_AUTH_STATUS', status)
    },
    
    // 更新真实姓名
    updateRealName({ commit }, realName) {
      commit('UPDATE_REAL_NAME', realName)
    },
    
    // 全局方法：根据用户角色和认证状态进行跳转
    navigateByAuthStatus({ state, getters }, router) {
      const roleCode = getters.roleCode;
      // 优先从state获取用户信息，如果state中没有再从localStorage获取
      let userInfo = state.userInfo;
      if (!userInfo) {
        const homelabUserInfo = localStorage.getItem('homelab_user_info');
        if (homelabUserInfo) {
          try {
            userInfo = JSON.parse(homelabUserInfo);
          } catch (error) {
            console.error('解析用户信息失败:', error);
            router.push({ name: 'Admin' }); // 默认跳转
            return;
          }
        }
      }
      
      if (userInfo) {
        const { teacherAuth, schoolAdminAuth, authStatus } = userInfo;
        // 根据角色和认证状态进行跳转
        if (roleCode === 'TEACHER') {
          if (!teacherAuth || Object.keys(teacherAuth).length === 0 || teacherAuth.status === 'PENDING' || teacherAuth.status === 'REJECTED') {
            // 如果是老师但未认证、待审核或被拒绝，则跳转到认证页面
            router.push({ name: 'TeacherAuthNormal' });
          } else {
            // 如果老师已认证，则跳转到工作台
            router.push({ name: 'TeacherWorkbench' });
          }
        } else if (roleCode === 'SCHOOL_ADMIN') {

          if (!schoolAdminAuth || Object.keys(schoolAdminAuth).length === 0 || schoolAdminAuth.status === 'PENDING' || schoolAdminAuth.status === 'REJECTED') {
            // 如果是学校管理员但未认证、待审核或被拒绝，则跳转到认证页面
            router.push({ name: 'TeacherAuthAdmin' });
          } else {
            // 如果学校管理员已认证，则跳转到仪表盘
            router.push({ name: 'SchoolDashboard' });
          }
        } else if (roleCode === 'STUDENT') {
          if (!authStatus || authStatus === 'UN_AUTH') {
            // 如果是学生但未认证，则跳转到学生认证页面
            router.push({ name: 'UserAuth' });
          } else {
            // 如果学生已认证，则跳转到控制台
            router.push({ name: 'Console' });
          }
        } else if (roleCode === 'CITY_ADMIN' || roleCode === 'DISTRICT_ADMIN' || roleCode === 'ADMIN') {
          router.push({ path: '/admin' });
        } else if (roleCode === 'ORGANIZER') {
          router.push({ path: '/admin/users' });
        } else {
          // 默认跳转到首页
          router.push('/');
        }
      } else {
        // 如果没有用户信息，默认跳转到首页
        router.push('/');
      }
    },
    
    // 登出
    logout({ commit }) {
      return new Promise((resolve) => {
        commit('CLEAR_AUTH')
        resolve()
      })
    },

    // 加载活动配置（每次都从后端获取最新数据）
    async loadContestConfig({ commit, state }) {
      // 每次都调用接口获取最新数据，不使用缓存
      try {
        let res
        
        // 根据用户角色选择不同的接口
        const roleCode = state.userInfo?.roleCode || localStorage.getItem('ROLE_CODE')
        if (roleCode === 'ORGANIZER') {
          // 只有组委会使用管理端接口
          res = await getContestDetail()
        } else {
          // 其他所有角色和场景都使用通用接口
          res = await getCommonContestDetail()
        }
        
        if (res && res.body) {
          commit('SET_CONTEST_CONFIG', res.body)
          console.log('活动配置已更新:', res.body)
          return res.body
        }
      } catch (error) {
        console.error('加载活动配置失败:', error)
        // 如果接口失败，尝试使用缓存作为降级方案
        const cached = localStorage.getItem('contest_config')
        if (cached) {
          try {
            const config = JSON.parse(cached)
            console.warn('接口失败，使用缓存的活动配置:', config)
            commit('SET_CONTEST_CONFIG', config)
            return config
          } catch (e) {
            console.error('解析缓存失败:', e)
          }
        }
      }
      return null
    }
  },
  
  getters: {
    isLoggedIn: state => !!state.token,
    userInfo: state => state.userInfo || localStorage.getItem('homelab_user_info'),
    userRole: state => state.userInfo?.role || null,
    loginName: () => localStorage.getItem('LOGIN_NAME'),
    roleCode: state => state.userInfo?.roleCode || localStorage.getItem('ROLE_CODE'), 
    realName: state => state.userInfo?.realName || localStorage.getItem('realName'), 
    userId: () => state.userInfo?.userId || localStorage.getItem('USER_ID'),
    contestConfig: state => state.contestConfig
  }
})